import DataProvider from './DataProvider';
import platformConfig from '../config/platformConfig';
import { fetchSelfHostedProjectCSS } from '../utils/projectAssets';

/**
 * ArchiveProvider — reads data from pre-cached JSON files.
 *
 * Expects files in the format produced by scripts/lib/chain.js:
 *   {networkId}-{projectId}-project.json  — project metadata
 *   {networkId}-{projectId}-tokens.json   — token mint data
 *   {networkId}-{projectId}-script.json   — decoded art script
 *
 * Files are fetched from a configurable base path (default: /cache/).
 */
class ArchiveProvider extends DataProvider {
  constructor(options = {}) {
    super();
    this.networkId = options.networkId || 1;
    this.basePath = options.basePath || `${process.env.PUBLIC_URL}/cache/`;
    this._projects = [];
    this._artists = [];
    this.cacheGeneratedAt = null;
    this._owners = {};
    this.ownersCapturedAt = null;
    this._scripts = {};
    this._tokens = {};
    // Page posts (the info and style content) keyed by page name, so the
    // Project Information tab has something to show in read-only archive mode.
    this._pageposts = {};
  }

  get sourceType() { return 'archive'; }
  get isReadOnly() { return true; }

  async initialize() {
    try {
      // The manifest records when refresh-cache.js last regenerated this
      // snapshot. Absent on older caches — the UI just omits the date then.
      try {
        const manifest = await this._fetchJSON(`${this.networkId}-manifest.json`);
        this.cacheGeneratedAt = manifest && manifest.generatedAt ? manifest.generatedAt : null;
      } catch (e) {
        this.cacheGeneratedAt = null;
      }

      // Discover available projects by trying to load them
      // We'll try project IDs 1 through 20 (adjust as needed)
      const projectPromises = [];
      for (let i = 1; i <= 20; i++) {
        projectPromises.push(
          this._fetchJSON(`${this.networkId}-${i}-project.json`)
            .then(data => ({ id: i, data }))
            .catch(() => null)
        );
      }

      const results = await Promise.all(projectPromises);
      for (const result of results) {
        // Guard against blank project files: a cache generated over a range of
        // ids can contain empty structs for ids the contract doesn't know, and
        // an empty object is still truthy.
        if (result && result.data && result.data.name) {
          this._projects[result.id - 1] = result.data;
          // Artist address not available in cache
          this._artists[result.id - 1] = null;
        }
      }

      // Ownership snapshot. Unlike mints this is not append-only — a transfer
      // invalidates it silently — so it carries its own capture time, and the
      // UI is expected to caption it "as of" that date rather than presenting
      // it as current.
      await Promise.all(
        this._projects.map(async (project, index) => {
          if (!project) return;
          try {
            const data = await this._fetchJSON(`${this.networkId}-${index + 1}-owners.json`);
            if (data && data.owners) {
              Object.assign(this._owners, data.owners);
              if (!this.ownersCapturedAt || data.capturedAt < this.ownersCapturedAt) {
                // Report the oldest capture, so the date never overstates freshness.
                this.ownersCapturedAt = data.capturedAt;
              }
            }
          } catch (e) {
            // No owner cache for this project — getOwnerOfToken returns null.
          }
        })
      );

      // Page posts (info, style). Cached per project and keyed by page name so
      // getPagePosts can answer without chain access. Absent for projects whose
      // cache predates this file — those simply have no info tab offline.
      await Promise.all(
        this._projects.map(async (project, index) => {
          if (!project || !project.name) return;
          try {
            const data = await this._fetchJSON(`${this.networkId}-${index + 1}-pageposts.json`);
            if (Array.isArray(data) && data.length) this._pageposts[project.name] = data;
          } catch (e) {
            // No page-post cache for this project — getPagePosts returns [].
          }
        })
      );

      return { success: true, needsModal: null };
    } catch (error) {
      console.error('[ArchiveProvider] Initialize failed:', error);
      return { success: false, error: error.message };
    }
  }

  async _fetchJSON(filename) {
    // The single-file build inlines the cache, because fetch() is denied on a
    // file:// origin — without this the whole archive path is unavailable when
    // the app is opened straight off disk.
    const embedded = typeof window !== 'undefined' && window.__ARCHIVE_DATA;
    if (embedded && Object.prototype.hasOwnProperty.call(embedded, filename)) {
      return embedded[filename];
    }
    if (embedded) throw new Error(`${filename} not embedded`);

    const url = `${this.basePath}${filename}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  async getPlatformConfig() {
    const currentProjectId = this._projects.filter(Boolean).length;
    return {
      platform: { ...platformConfig },
      maxTotalSupply: 0,
      currentTokenId: 0,
      currentProjectId,
      artistPercent: 0,
      immutablesWEB: "",
      immutablesURI: "",
      useMetadataServer: false,
      curator: null,
      curatorPercent: null,
      beneficiary: null,
      beneficiaryPercent: null,
      projectFeeInWei: "0",
      projectFeeInEth: "0",
      featuredProjectId: 1,
      secondaryRoyaltyPercent: 0,
    };
  }

  async getUserRoles(account, projectId) {
    return {
      userIsAdmin: false,
      userIsContractOwner: false,
      userIsPayee: false,
      userIsAuthorizedArtist: false,
      userIsProjectArtist: false,
      artistScreeningEnabled: false,
      contractOwner: null,
    };
  }

  async getProjects() {
    return {
      projects: this._projects.filter(Boolean),
      artists: this._artists.filter(() => true), // keep indices aligned
    };
  }

  async getProjectDetails(projectId) {
    const project = this._projects[projectId - 1];
    if (!project) throw new Error(`Project ${projectId} not found in archive`);

    // refresh-cache.js records these alongside the project struct: the image
    // pointer and artist address live in separate contract mappings, so they
    // are fetched and cached separately. Reporting them as null made the
    // project table claim no marketplace images were configured when they were.
    return {
      projectArtistAddress: project.projectArtistAddress || null,
      projectPricePerEditionInWei: "0",
      projectPricePerEditionInEth: "0",
      projectAdditionalPayee: null,
      projectAdditionalPayeePercent: null,
      projectRoyaltyAddress: null,
      projectIdToImageURLBase: project.projectIdToImageURLBase || null,
      projectIdToImageURLExt: project.projectIdToImageURLExt || null,
      projectIdUseImageURLInGridView: !!project.projectIdUseImageURLInGridView,
      clientViewImageURLInDetailView: !!project.projectIdUseImageURLInGridView,
      royaltyContractBalance: "0",
    };
  }

  async getProjectTokens(projectId, currentEditions, onProgress) {
    if (onProgress) onProgress('loading cached tokens...');

    // Check if already loaded
    if (this._tokens[projectId]) {
      if (onProgress) onProgress('loading editions 100.00% ...');
      return this._tokens[projectId];
    }

    try {
      const data = await this._fetchJSON(`${this.networkId}-${projectId}-tokens.json`);
      const tokens = data.tokens || [];
      this._tokens[projectId] = tokens;
      if (onProgress) onProgress('loading editions 100.00% ...');
      return tokens;
    } catch (error) {
      console.warn(`[ArchiveProvider] No tokens cache for project ${projectId}`);
      if (onProgress) onProgress('loading editions 100.00% ...');
      return [];
    }
  }

  async getProjectScript(scriptTransactionHash) {
    // Search all cached scripts for a match
    for (let pid = 1; pid <= this._projects.length; pid++) {
      const project = this._projects[pid - 1];
      if (project && project.scriptTransactionHash === scriptTransactionHash) {
        if (this._scripts[pid]) {
          return this._scripts[pid];
        }
        try {
          const data = await this._fetchJSON(`${this.networkId}-${pid}-script.json`);
          const result = {
            script: data.script || null,
            poster: null,
            page: data.page || null,
            blockNumber: null,
          };
          this._scripts[pid] = result;
          return result;
        } catch (error) {
          console.warn(`[ArchiveProvider] No script cache for project ${pid}`);
        }
      }
    }
    return { script: null, poster: null, page: null, blockNumber: null };
  }

  async getPagePosts(pageName, scriptBlockNumber) {
    return (pageName && this._pageposts[pageName]) || [];
  }

  async getOwnerOfToken(tokenId) {
    if (tokenId === null || tokenId === undefined) return null;
    const owner = this._owners[String(tokenId)];
    return owner || null;
  }

  async getEditionEventHistory(editionId, projectTokens) {
    return []; // Not available offline
  }

  async getTeammembers() {
    return {};
  }

  async getAuthorizedArtists() {
    return {};
  }

  async getContractBalance() {
    return { contractBalance: "0", contractBalanceWei: "0" };
  }

  async getProjectCSS(imageURLBase, isMobile, projectId) {
    // Archive mode has no chain access to resolve a project's IPFS CID, so the
    // self-hosted mirror is the only stylesheet source available here.
    return await fetchSelfHostedProjectCSS(this.networkId, projectId, isMobile);
  }
}

export default ArchiveProvider;
